import { createModel } from '@rematch/core';
import { post } from 'utils/http';
import {
  IModuleProject,
  IModuleProjectDetail,
  IModuleProjectListResponse,
  IModuleTaskListResponse,
  ICreateModuleProjectParams,
  ICreateModuleProjectResponse,
  IGetModuleProjectListParams,
  IGetModuleProjectDetailParams,
  IUpdateModuleProjectParams,
  IAllocateModuleTasksParams,
  ICollectModuleTasksParams,
  ICollectModuleTasksResponse,
  IGetModuleTaskListParams,
  IGetAllAnnotationTemplateListParams,
  IGetAllAnnotationTemplateListResponse,
  IGetModuleFilterTaskDetailParams,
  IModuleFilterTaskDetailResponse,
  IBatchUpdateModuleFilterResultParams,
  ISubmitModuleFilterTaskParams,
  IGetModuleAnnotationTaskDetailParams,
  IModuleAnnotationTaskDetailResponse,
  ISaveModuleAnnotationResultParams,
  ISubmitModuleAnnotationTaskParams,
  IGetModuleInspectTaskDetailParams,
  IModuleInspectTaskDetailResponse,
  ISubmitModuleInspectResultParams,
  ISaveModuleInspectResultParams,
  IBatchSubmitModuleInspectParams,
  IBatchSubmitModuleInspectResponse,
  IModuleStorageCorpusParams,
  IModuleStorageCorpusResponse,
  IGetModuleStorageHistoryParams,
  IGetModuleStorageHistoryRes,
  // 新增类型
  IGetModuleCorpusStatsParams,
  IModuleCorpusStatsResponse,
  IClaimModuleOperateTaskParams,
  IClaimModuleOperateTaskResponse,
  IClaimModuleInspectTaskParams,
  IClaimModuleInspectTaskResponse,
  IIncreaseModuleSampleRatioParams,
  IIncreaseModuleSampleRatioResponse,
  IExportModuleOperatorStatsParams,
  IExportModuleOperatorStatsResponse,
  IGetModuleAnnotationStatsParams,
  IModuleAnnotationStatsResponse,
  IGetAnnotationTemplateListParams,
  IAnnotationTemplateListResponse,
  // 模块详细统计类型
  IGetModuleDetailStatsParams,
  IGetModuleDetailStatsResponse,
  ICreateAnnotationTemplateParams,
  IUpdateAnnotationTemplateParams,
} from 'types/pipelineModule';

interface IPipelineModuleState {
  projectList: IModuleProject[];
  currentProject: IModuleProjectDetail | null;
}

const initialState: IPipelineModuleState = {
  projectList: [],
  currentProject: null,
};

export default createModel()({
  state: initialState,
  reducers: {
    setState: (state: IPipelineModuleState, payload: Partial<IPipelineModuleState>) => ({
      ...state,
      ...payload,
    }),
    clearCurrentProject: (state: IPipelineModuleState) => ({
      ...state,
      currentProject: null,
    }),
  },
  effects: () => ({
    // ==================== 项目管理 ====================

    /**
     * 创建带模块编排的项目
     */
    async createProjectWithModules(params: ICreateModuleProjectParams): Promise<ICreateModuleProjectResponse | null> {
      const res = await post('/api/pipelineModule/createProjectWithModules', params);
      if (res) {
        return res as ICreateModuleProjectResponse;
      }
      return null;
    },

    /**
     * 获取模块项目列表
     */
    async getModuleProjectList(params: IGetModuleProjectListParams) {
      const res = await post('/api/pipelineModule/getModuleProjectList', params);
      if (res) {
        const data: IModuleProjectListResponse = res;
        this.setState({
          projectList: data.list || [],
        });
        return {
          projectList: data.list || [],
          page: {
            totalItemNum: data.total || 0,
            pageNum: params.pageNum,
            pageSize: params.pageSize,
          },
        };
      }
      return null;
    },

    /**
     * 获取项目详情（包含模块列表）
     */
    async getModuleProjectDetail(params: IGetModuleProjectDetailParams): Promise<IModuleProjectDetail | null> {
      const res = await post('/api/pipelineModule/getModuleProjectDetail', params);
      if (res) {
        const project: IModuleProjectDetail = res;
        this.setState({
          currentProject: project,
        });
        return project;
      }
      return null;
    },

    /**
     * 更新模块项目（支持更新项目信息和模块人员配置）
     */
    async updateModuleProject(params: IUpdateModuleProjectParams) {
      const res = await post('/api/pipelineModule/updateModuleProject', params);
      return res;
    },

    /**
     * 删除模块项目
     */
    async deleteModuleProject(params: { projectId: number }) {
      const res = await post('/api/pipelineModule/deleteModuleProject', params);
      return res;
    },

    // ==================== 任务分配 ====================

    /**
     * 分配模块操作任务（支持两种模式）
     * 模式一：指定操作人分配（传 assignments）
     * 模式二：批量下发待认领任务（传 taskCount + corpusPerTask）
     */
    async allocateModuleTasks(params: IAllocateModuleTasksParams) {
      const res = await post('/api/pipelineModule/allocateModuleTasks', params);
      return res;
    },

    /**
     * 回收未完成任务
     */
    async collectModuleRemainingTasks(params: ICollectModuleTasksParams): Promise<ICollectModuleTasksResponse | null> {
      const res = await post('/api/pipelineModule/collectModuleRemainingTasks', params);
      if (res) {
        return res as ICollectModuleTasksResponse;
      }
      return null;
    },

    /**
     * 获取模块任务列表
     */
    async getModuleTaskList(params: IGetModuleTaskListParams) {
      const res = await post('/api/pipelineModule/getModuleTaskList', params);
      if (res) {
        const data: IModuleTaskListResponse = res;
        return {
          taskList: data.list || [],
        };
      }
      return null;
    },

    // ==================== 筛选任务操作 ====================

    /**
     * 获取筛选任务详情
     */
    async getModuleFilterTaskDetail(
      params: IGetModuleFilterTaskDetailParams,
    ): Promise<IModuleFilterTaskDetailResponse | null> {
      const res = await post('/api/pipelineModule/getModuleFilterTaskDetail', params);
      if (res) {
        return {
          taskId: res.taskId,
          moduleId: res.moduleId,
          total: res.total || 0,
          completedCount: res.completedCount || 0,
          keptCount: res.keptCount || 0,
          list: res.list || [],
        };
      }
      return null;
    },

    /**
     * 批量更新筛选结果
     */
    async batchUpdateModuleFilterResult(params: IBatchUpdateModuleFilterResultParams) {
      const res = await post('/api/pipelineModule/batchUpdateModuleFilterResult', params);
      return res;
    },

    /**
     * 提交筛选任务
     */
    async submitModuleFilterTask(params: ISubmitModuleFilterTaskParams) {
      const res = await post('/api/pipelineModule/submitModuleFilterTask', params);
      return res;
    },

    // ==================== 标注任务操作 ====================

    /**
     * 获取标注任务详情
     */
    async getModuleAnnotationTaskDetail(
      params: IGetModuleAnnotationTaskDetailParams,
    ): Promise<IModuleAnnotationTaskDetailResponse | null> {
      const res = await post('/api/pipelineModule/getModuleAnnotationTaskDetail', params);
      if (res) {
        return {
          taskId: res.taskId,
          moduleId: res.moduleId,
          projectId: res.projectId,
          total: res.total || 0,
          completedCount: res.completedCount || 0,
          annotationTemplate: res.annotationTemplate || [],
          annotationTemplateSubType: res.annotationTemplateSubType,
          corpusType: res.corpusType,
          list: res.list || [],
        };
      }
      return null;
    },

    /**
     * 保存标注结果
     */
    async saveModuleAnnotationResult(params: ISaveModuleAnnotationResultParams) {
      const res = await post('/api/pipelineModule/saveModuleAnnotationResult', params);
      return res;
    },

    /**
     * 提交标注任务
     */
    async submitModuleAnnotationTask(params: ISubmitModuleAnnotationTaskParams) {
      const res = await post('/api/pipelineModule/submitModuleAnnotationTask', params);
      return res;
    },

    // ==================== 质检任务操作 ====================

    /**
     * 获取质检任务详情
     */
    async getModuleInspectTaskDetail(
      params: IGetModuleInspectTaskDetailParams,
    ): Promise<IModuleInspectTaskDetailResponse | null> {
      const res = await post('/api/pipelineModule/getModuleInspectTaskDetail', params);
      if (res) {
        // 将后台返回的 operateResult 映射到 annotationResult / originalAnnotationResult，
        // 使 ModuleAnnotationWorkbench 质检模式能正确回显标注数据
        const list = (res.list || []).map((item: Record<string, unknown>) => {
          // 后台返回的 inspectConclusion 是质检结论（1=通过，2=不通过）
          const inspectConclusionValue = item.inspectConclusion as number | null;
          // 后台返回的 inspectResult 或 inspectAnnotationResult 是质检标注结果
          const inspectResultValue = (item.inspectResult ?? item.inspectAnnotationResult ?? null) as Record<string, unknown> | null;
          return {
            ...item,
            // 后台 operateResult 直接返回标注对象（如 {"视频描述": "xxx"}），
            // 映射到 annotationResult 和 originalAnnotationResult 供工作台回显使用
            annotationResult: item.annotationResult ?? item.operateResult ?? null,
            originalAnnotationResult: item.operateResult ?? null,
            // inspectResult 映射为质检标注结果（质检员修改后的标注数据）
            inspectResult: inspectResultValue,
            // inspectConclusion 映射为质检结论（1=通过，2=不通过）
            inspectConclusion: inspectConclusionValue,
            // 映射质检状态标记，供 CorpusList 显示
            isInspectPassed: item.isInspectPassed ?? inspectConclusionValue === 1,
            isInspectRejected: item.isInspectRejected ?? inspectConclusionValue === 2,
          };
        });
        return {
          taskId: res.taskId,
          moduleId: res.moduleId,
          inspectRound: res.inspectRound,
          taskTotal: res.taskTotal || 0,
          sampleTotal: res.sampleTotal || 0,
          total: res.total || 0,
          completedCount: res.completedCount || 0,
          sampleRatio: res.sampleRatio || 0,
          isCompleted: res.isCompleted || false,
          annotationTemplate: res.annotationTemplate || null,
          annotationTemplateSubType: res.annotationTemplateSubType,
          corpusType: res.corpusType,
          list,
        };
      }
      return null;
    },

    /**
     * 保存质检结果草稿
     */
    async saveModuleInspectResult(params: ISaveModuleInspectResultParams) {
      const res = await post('/api/pipelineModule/saveModuleInspectResult', params);
      return res;
    },

    /**
     * 完成本轮质检（提交质检任务）
     */
    async submitModuleInspectTask(
      params: IBatchSubmitModuleInspectParams,
    ): Promise<IBatchSubmitModuleInspectResponse | null> {
      const res = await post('/api/pipelineModule/submitModuleInspectTask', params);
      if (res) {
        return {
          code: res.code || 0,
          hasRejected: res.hasRejected || false,
          hasNextRound: res.hasNextRound || false,
        };
      }
      return null;
    },

    // ==================== 入库 ====================

    /**
     * 模块入库
     */
    async moduleStorageCorpus(params: IModuleStorageCorpusParams): Promise<IModuleStorageCorpusResponse | null> {
      const res = await post('/api/pipelineModule/moduleStorageCorpus', params);
      if (res) {
        return {
          code: res.code || 0,
          newCorpusSetId: res.newCorpusSetId,
        };
      }
      return null;
    },

    /**
     * 获取模块入库历史
     */
    async getModuleStorageHistory(params: IGetModuleStorageHistoryParams): Promise<IGetModuleStorageHistoryRes | null> {
      const res = await post('/api/pipelineModule/getModuleStorageHistory', params);
      if (res) {
        return {
          code: res.code || 0,
          msg: res.msg,
          historyList: res.historyList || [],
        };
      }
      return null;
    },

    // ==================== 标注模板 ====================

    /**
     * 获取标注模板列表（公共接口）
     */
    async getAnnotationTemplateList(
      params: IGetAnnotationTemplateListParams,
    ): Promise<IAnnotationTemplateListResponse | null> {
      const res = await post('/api/pipeline-template/getTemplateList', params);
      if (res) {
        return {
          total: res.total || 0,
          list: res.list || [],
        };
      }
      return null;
    },

    /**
     * 获取标注模板列表（公共接口）
     */
    async getAllTemplateList(
      params: IGetAllAnnotationTemplateListParams,
    ): Promise<IGetAllAnnotationTemplateListResponse | null> {
      const res = await post('/api/pipeline-template/getAllTemplateList', params);
      if (res) {
        return res || [];
      }
      return null;
    },

    // ==================== 辅助方法 ====================

    /**
     * 获取语料集列表（复用 pipeline 的接口）
     */
    async getCorpusSetList(data = {}) {
      const rst = await post('/api/pipeline/getCorpusSetList', data);
      if (rst?.corpusSetList) {
        return {
          corpusSetList: rst.corpusSetList || [],
          page: rst.page || { totalPageNum: 0, pageNum: 1, pageSize: 10 },
        };
      }
      return {
        corpusSetList: [],
        page: { totalPageNum: 0, pageNum: 1, pageSize: 10 },
      };
    },

    // ==================== 语料统计 ====================

    /**
     * 获取模块语料统计
     */
    async getModuleCorpusStats(params: IGetModuleCorpusStatsParams): Promise<IModuleCorpusStatsResponse | null> {
      const res = await post('/api/pipelineModule/getModuleCorpusStats', params);
      if (res) {
        return {
          totalCorpusCount: res.totalCorpusCount || 0,
          pendingCorpusCount: res.pendingCorpusCount || 0,
        };
      }
      return null;
    },

    // ==================== 任务认领 ====================

    /**
     * 认领筛选/标注任务
     */
    async claimModuleOperateTask(
      params: IClaimModuleOperateTaskParams,
    ): Promise<IClaimModuleOperateTaskResponse | null> {
      const res = await post('/api/pipelineModule/claimModuleOperateTask', params);
      if (res) {
        return {
          code: res.code || 0,
          taskId: res.taskId,
          claimedCount: res.claimedCount || 0,
        };
      }
      return null;
    },

    /**
     * 认领质检任务
     */
    async claimModuleInspectTask(
      params: IClaimModuleInspectTaskParams,
    ): Promise<IClaimModuleInspectTaskResponse | null> {
      const res = await post('/api/pipelineModule/claimModuleInspectTask', params);
      if (res) {
        return {
          code: res.code || 0,
          taskId: res.taskId,
          claimedCount: res.claimedCount || 0,
          sampleRatio: res.sampleRatio || 100,
        };
      }
      return null;
    },

    // ==================== 质检功能增强 ====================

    /**
     * 提高抽检比例
     */
    async increaseModuleSampleRatio(
      params: IIncreaseModuleSampleRatioParams,
    ): Promise<IIncreaseModuleSampleRatioResponse | null> {
      const res = await post('/api/pipelineModule/increaseModuleSampleRatio', params);
      if (res) {
        return {
          code: res.code || 0,
          addedCount: res.addedCount || 0,
          newSampleRatio: res.newSampleRatio || 0,
        };
      }
      return null;
    },

    // ==================== 统计功能 ====================

    /**
     * 导出操作人员人效统计
     */
    async exportModuleOperatorStats(
      params: IExportModuleOperatorStatsParams,
    ): Promise<IExportModuleOperatorStatsResponse | null> {
      const res = await post('/api/pipelineModule/exportModuleOperatorStats', params);
      if (res) {
        return {
          moduleId: res.moduleId,
          moduleName: res.moduleName || '',
          list: res.list || [],
        };
      }
      return null;
    },

    /**
     * 获取标注维度统计
     */
    async getModuleAnnotationStats(
      params: IGetModuleAnnotationStatsParams,
    ): Promise<IModuleAnnotationStatsResponse | null> {
      const res = await post('/api/pipelineModule/getModuleAnnotationStats', params);
      if (res) {
        return {
          moduleId: res.moduleId,
          moduleType: res.moduleType,
          totalCount: res.totalCount || 0,
          completedCount: res.completedCount || 0,
          dimensionStats: res.dimensionStats || [],
          accuracyStats: res.accuracyStats || {
            totalInspected: 0,
            passedCount: 0,
            overallAccuracy: 0,
            operatorAccuracy: [],
            dimensionAccuracy: [],
          },
        };
      }
      return null;
    },

    /**
     * 获取模块详细统计数据（人效统计 + 标注统计）
     * 按需加载，点击"人效看板"或"数据统计"按钮时调用
     */
    async getModuleDetailStats(params: IGetModuleDetailStatsParams): Promise<IGetModuleDetailStatsResponse | null> {
      const res = await post('/api/pipelineModule/getModuleDetailStats', params);
      if (res) {
        return {
          operatorStats: res.operatorStats || [],
          annotationStats: res.annotationStats,
          annotationAccuracyStats: res.annotationAccuracyStats,
          cvAnnotationStats: res.cvAnnotationStats,
        };
      }
      return null;
    },

    // 创建标注模板
    async createAnnotationTemplate(params: ICreateAnnotationTemplateParams) {
      const res = await post('/api/pipeline-template/createTemplate', params);
      return res;
    },

    // 更新标注模板
    async updateAnnotationTemplate(params: IUpdateAnnotationTemplateParams) {
      const res = await post('/api/pipeline-template/updateTemplate', params);
      return res;
    },

    // 启用标注模板
    async enableAnnotationTemplate(params: { id: number }) {
      const res = await post('/api/pipeline-template/enableTemplate', params);
      return res;
    },

    // 禁用标注模板
    async disableAnnotationTemplate(params: { id: number }) {
      const res = await post('/api/pipeline-template/disableTemplate', params);
      return res;
    },
  }),
});
